import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const flowSteps = [
  { label: '课堂原话', icon: 'ri-mic-line' },
  { label: '案例实体识别', icon: 'ri-scan-line' },
  { label: '权威公开资料', icon: 'ri-global-line' },
  { label: '关键数据核验', icon: 'ri-check-double-line' },
  { label: '课件资源', icon: 'ri-slideshow-3-line' },
  { label: '集体备课案例库', icon: 'ri-folder-open-line' },
];

export default function P41CaseHighSpeedRail({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100/60 text-accent-700 text-sm font-body font-semibold">
              <i className="ri-file-text-line text-sm md:text-base" /> 完整闭环案例 B
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-[3.25rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            从<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">"京沪高铁"</span>到权威资料与教学资源
          </h2>
        </div>

        {/* 流程 — 横向双行放大布局 */}
        <div className={`max-w-6xl mx-auto mb-8 md:mb-10 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex flex-col items-center gap-8 md:gap-10">
            {/* 第一行：4个 */}
            <div className="flex items-center gap-2 md:gap-5">
              {flowSteps.slice(0, 4).map((step, i) => (
                <div key={step.label} className="flex items-center gap-2 md:gap-5">
                  <div className="flex flex-col items-center text-center gap-3 px-6 py-5 md:px-8 md:py-7 rounded-2xl glass-liquid border-primary-200/30 transition-all duration-300 hover:scale-105 min-w-[150px] md:min-w-[200px]">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary-100 flex items-center justify-center font-mono font-bold text-xl md:text-2xl text-primary-600 mb-1">
                      {i + 1}
                    </div>
                    <i className={`${step.icon} text-4xl md:text-5xl text-primary-500`} />
                    <p className="text-xl md:text-2xl font-body font-bold text-foreground-950 whitespace-nowrap">{step.label}</p>
                  </div>
                  {i < 3 && (
                    <i className="ri-arrow-right-s-line text-foreground-950 text-3xl md:text-4xl flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>

            {/* 向下箭头 */}
            <div className="flex items-center justify-center">
              <i className="ri-arrow-down-line text-primary-500 text-3xl md:text-4xl" />
            </div>

            {/* 第二行：2个 */}
            <div className="flex items-center gap-2 md:gap-5">
              {flowSteps.slice(4).map((step, i) => (
                <div key={step.label} className="flex items-center gap-2 md:gap-5">
                  <div className="flex flex-col items-center text-center gap-3 px-6 py-5 md:px-8 md:py-7 rounded-2xl glass-liquid-gold border-accent-200/30 transition-all duration-300 hover:scale-105 min-w-[150px] md:min-w-[200px]">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent-100 flex items-center justify-center font-mono font-bold text-xl md:text-2xl text-accent-600 mb-1">
                      {i + 5}
                    </div>
                    <i className={`${step.icon} text-4xl md:text-5xl text-accent-500`} />
                    <p className="text-xl md:text-2xl font-body font-bold text-foreground-950 whitespace-nowrap">{step.label}</p>
                  </div>
                  {i < 1 && (
                    <i className="ri-arrow-right-s-line text-foreground-950 text-3xl md:text-4xl flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex items-center gap-2 px-6 py-4 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-lightbulb-line text-accent-500 text-base md:text-lg" />
            <span className="text-sm md:text-base font-body text-foreground-950">
              目标不是发现错误，而是让优秀案例变得可追溯、可复用
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}